// Audio Message Component
// Following existing patterns from MessageItem

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { AudioMessageProps } from '../../../types/messaging';
import {
  AudioMessageContainer,
  AudioPlayer,
  AudioControls,
  PlayButton,
  AudioProgress,
  AudioProgressBar,
  AudioProgressFill,
  AudioTime,
  AudioInfo,
  AudioName,
  AudioSize,
  AudioActions,
  AudioActionButton
} from '../../../styles/Messaging/MessageTypes.styles';

export const AudioMessage: React.FC<AudioMessageProps> = ({
  message,
  isOwn,
  onAction
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const audioRef = useRef<HTMLAudioElement>(null);

  const handlePlayPause = useCallback(() => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
  }, [isPlaying]);

  const handleTimeUpdate = useCallback(() => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  }, []);

  const handleLoadedMetadata = useCallback(() => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
      setIsLoading(false);
    }
  }, []);

  const handleLoadStart = useCallback(() => {
    setIsLoading(true);
  }, []);

  const handleEnded = useCallback(() => {
    setIsPlaying(false);
    setCurrentTime(0);
  }, []);

  const handleProgressClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!audioRef.current || duration === 0) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percentage = clickX / rect.width;
    const newTime = percentage * duration;
    
    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  }, [duration]);

  const handleDownload = useCallback(() => {
    const attachment = message.attachments?.[0];
    if (attachment?.url) {
      const link = document.createElement('a');
      link.href = attachment.url;
      link.download = attachment.filename || 'audio';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }, [message.attachments]);

  const handleShare = useCallback(() => {
    const attachment = message.attachments?.[0];
    if (navigator.share && attachment?.url) {
      navigator.share({
        title: attachment.filename || 'Shared Audio',
        url: attachment.url
      }).catch(console.error);
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(attachment?.url || '').catch(console.error);
    }
  }, [message.attachments]);

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const attachment = message.attachments?.[0];
  if (!attachment) {
    return (
      <AudioMessageContainer $isOwn={isOwn}>
        <div style={{ padding: '1rem', textAlign: 'center', color: '#6c757d' }}>
          Audio not available
        </div>
      </AudioMessageContainer>
    );
  }

  const { filename, size, url } = attachment;
  const progressPercentage = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <AudioMessageContainer $isOwn={isOwn}>
      <audio
        ref={audioRef}
        src={url}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onLoadStart={handleLoadStart}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onEnded={handleEnded}
        preload="metadata"
      />
      
      <AudioPlayer>
        <AudioControls>
          <PlayButton onClick={handlePlayPause} disabled={isLoading}>
            {isLoading ? '⏳' : isPlaying ? '⏸️' : '▶️'}
          </PlayButton>
          
          <AudioProgress onClick={handleProgressClick}>
            <AudioProgressBar>
              <AudioProgressFill $progress={progressPercentage} />
            </AudioProgressBar>
          </AudioProgress>
          
          <AudioTime>
            {formatTime(currentTime)} / {formatTime(duration)}
          </AudioTime>
        </AudioControls>
        
        <AudioInfo>
          <AudioName>{filename}</AudioName>
          <AudioSize>{formatFileSize(size)}</AudioSize>
        </AudioInfo>
        
        <AudioActions>
          <AudioActionButton onClick={handleDownload} title="Download">
            ⬇️
          </AudioActionButton>
          <AudioActionButton onClick={handleShare} title="Share">
            🔗
          </AudioActionButton>
        </AudioActions>
      </AudioPlayer>
      
      {message.content && (
        <div style={{ 
          marginTop: '0.5rem', 
          padding: '0.5rem', 
          background: 'rgba(0, 0, 0, 0.05)', 
          borderRadius: '4px',
          fontSize: '0.875rem'
        }}>
          {message.content}
        </div>
      )}
    </AudioMessageContainer>
  );
};
